import React, { ReactNode, CSSProperties, Key } from 'react';
import { Spin } from 'antd';
import AutoSizer from 'react-virtualized-auto-sizer';
import 'app/common/table/ManagedTable.css';
import {
    DataTypeProvider,
    IntegratedSorting,
    IntegratedSummary,
    SelectionState,
    Sorting,
    SortingState,
    SummaryItem,
    SummaryState,
    TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
    Grid,
    TableBandHeader,
    TableColumnResizing,
    TableHeaderRow,
    TableSummaryRow,
    TableSelection,
    VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import Utils from 'app/common/util/Utils';
import { TableUtils } from 'app/common/util/TableUtils';
import { DesktopBehaviour } from 'app/common/table/plugins/DesktopBehaviour';
import { Highlighter } from 'app/common/table/Highlighter';
import { ProvidesKey } from 'app/model/client/CORE';
import { Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import TableColumnTooltipComponent, { TableColumnTooltip } from './TableColumnTooltipComponent';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { WebBehaviour } from 'app/common/table/plugins/WebBehaviour';

const DEFAULT_FONT_SIZE = 8;

export const SINGLE_SELECTION: SingleSelectionMode = 'single';
type SingleSelectionMode = 'single';

export const MULTIPLE_SELECTION: MultipleSelectionMode = 'multiple';
type MultipleSelectionMode = 'multiple';

export interface SingleSelection<T> {
    selectionMode: SingleSelectionMode;
    onSelectionChange?(selected: T): void;
    selectedItem?: T;
    selectionMatcher?(element: T, currentSelection: T): boolean;
}

export interface MultipleSelection<T> {
    selectionMode: MultipleSelectionMode;
    onSelectionChange?(selected: Array<T>): void;
    selectedItems?: Array<T>;
    selectionMatcher?(element: T, currentSelection: Array<T>): boolean;
}

export type ManagedTableSummaryItem = SummaryItem;

export interface ManageTableSummaryProps {
    totalItems: ManagedTableSummaryItem[];
    //TODO : add the other summary fields/possibilities (e.g. custom summary logic) if needed
}

/**
 * Sort Mode
 * INTERNAL - uses DevExpress IntegratedSorting functionality
 * EXTERNAL - the class user will provide the sorted data (so controlled mode)
 * NONE - sorting deactivated.
 */
export enum SortMode {
    INTERNAL,
    EXTERNAL,
    NONE,
}
export type ManagedTableSorting = Sorting;

export interface ManagedTableProps<T> {
    data: Array<T>;
    columns: Array<ManagedColumn<T>>;
    keyProvider?: ProvidesKey<T>;
    selection?: SingleSelection<T> | MultipleSelection<T>; // 'SingleSelection' by default
    loading?: boolean;
    highlighter?: Highlighter<T>;
    highlight?: (object: T) => boolean;
    summaryRow?: ManageTableSummaryProps;
    /** undefined means INTERNAL */
    sortMode?: SortMode;
    /** Specifies the applied sorting. Only use for controlled mode, in conjunction with onSortChange.*/
    sorting?: Array<ManagedTableSorting>;
    /** Specifies initial sorting in the uncontrolled mode. */
    defaultSorting?: Array<ManagedTableSorting>;
    /** Handles sorting changes. */
    onSortingChange?: (sorting: Array<ManagedTableSorting>) => void;
    /* Specifies that column resize shall be disabled */
    columnResizeDisabled?: boolean;
    /* Font size in pixels */
    fontSize?: number;

    webBehaviour?: boolean;

    noDataMessage?: string;

    //if not provided getEstimatedRowHeight method will be used
    estimatedRowHeight?: number;
}

interface ManagedTableState<T> {
    selectedRowKeys: Array<Key>;
    columnWidth: Map<string, string | number>;

    // = copied over props, so that getDerivedStateFromProps can detect differences
    columns?: Array<ManagedColumn<T>>;
    selection?: SingleSelection<T> | MultipleSelection<T>; // 'SingleSelection' by default
    fontSize?: number;
}

export interface ManagedColumnExtra<T> {
    // If not provided, will default to column header
    // Needs to be provided if multiple column have the same header
    name?: string;

    // Enabled by default
    sortingEnabled?: boolean;

    // It can be a string, number or a ReactElement<P> if render returns a React.Component<P> (in which case a.props are accessible!)
    // This can be useful to implement a comparator that requires access to the full object (via a.props.object for example)
    comparator?: (a: any, b: any) => number;

    // Allows to organise column headers into bands
    columnBand?: string;

    // Custom style for cells
    cellStyle?: (value?: React.ReactNode, object?: T) => CSSProperties;

    // Class name for cells
    className?: (value?: React.ReactNode, object?: T) => string;

    // Enable word wrapping in the column cell (defaults to false)
    wordWrapEnabled?: boolean;

    // custom column tooltip
    tooltip?: TableColumnTooltip;

    //the formatter value input is the result of the getCellValue
    formatter?: (value: any, parent?: ManagedTable<T>) => React.ReactNode;

    csvEncoder?: (value: any) => string;
}

export class ManagedColumn<T> {
    header: string;
    width: string | number;
    getCellValue: (object: T, parent?: ManagedTable<T>) => object | string | ReactNode; //keeping the parent table because it's being use to get the value in some cases
    extra?: ManagedColumnExtra<T>;

    constructor(
        header: string,
        width: string | number,
        getCellValue: (object: T, parent?: ManagedTable<T>) => any,
        extra?: ManagedColumnExtra<T>,
    ) {
        this.header = header;
        this.width = width;
        this.getCellValue = getCellValue;
        this.extra = extra;
    }
}

function columnName<T>(column: ManagedColumn<T>) {
    return column.extra?.name || column.header;
}

/**
 * @deprecated Use DataTable instead. ManagedTable uses the DevExpress React Grid underneath, which is a licensed
 *             product, contains many features we don't need, and performs poorly during re-renders with large amounts
 *             of rows.
 */
export default class ManagedTable<T, P extends ManagedTableProps<T> = ManagedTableProps<T>> extends React.PureComponent<
    P,
    ManagedTableState<T>
> {
    private displayedRows: Array<T>;
    // @ts-ignore
    private virtualTableRef = React.createRef<VirtualTable>();

    private height; // the last known autoSizer height
    private topRowIdx = 0;

    constructor(props: P) {
        super(props);

        this.state = {
            selectedRowKeys: [],
            columnWidth: undefined,
            fontSize: undefined,
        };
    }

    static getDerivedStateFromProps(
        nextProps: ManagedTableProps<any>,
        prevState: ManagedTableState<any>,
    ): ManagedTableState<any> {
        let nextState = null;

        // Columns have been defined, compute column width and store in the state
        const fontSize = nextProps.fontSize ? nextProps.fontSize : DEFAULT_FONT_SIZE;
        if (nextProps.columns !== prevState.columns || prevState.fontSize !== nextProps.fontSize) {
            const columnWidth = new Map();
            nextProps.columns?.forEach(column => {
                columnWidth.set(
                    columnName(column),
                    prevState.columnWidth?.get(columnName(column)) || TableUtils.toWidthPx(column.width, fontSize),
                );
            });
            nextState = {};
            nextState.columns = nextProps.columns;
            nextState.columnWidth = columnWidth;
            nextState.fontSize = fontSize;
        }
        // We derive selection state from props, only when the selection is managed outside of the table component (via selection.selectedItem<s> property)
        // We assume here that nextProps.selection has been updated to reflect nextProps.data, otherwise selection might be lost
        if (nextProps.selection !== prevState.selection) {
            if (!nextState) nextState = {};
            nextState.selection = nextProps.selection;
            const selectedItems = nextProps.selection?.['selectedItem'] || nextProps.selection?.['selectedItems'];
            nextState.selectedRowKeys = TableUtils.rowKeysFromSelection(
                nextProps.data,
                selectedItems,
                nextProps.selection.selectionMatcher,
            );
        }
        return nextState;
    }

    componentDidUpdate(prevProps: Readonly<P>) {
        if (prevProps.data !== this.props.data && this.props.selection?.selectionMatcher) {
            const selectedItems = this.props.selection?.['selectedItem'] || this.props.selection?.['selectedItems'];
            this.setState({
                selectedRowKeys: TableUtils.rowKeysFromSelection(
                    this.props.data,
                    selectedItems,
                    this.props.selection.selectionMatcher,
                ),
            });
        }
    }

    protected getSortMode() {
        return Utils.ifExistsElse(this.props.sortMode, SortMode.INTERNAL);
    }

    public scrollToRow(row: T) {
        this.virtualTableRef.current.scrollToRow(this.props.data.indexOf(row));
    }

    public getEstimatedRowHeight(): number {
        const fontSize = this.props.fontSize ? this.props.fontSize : DEFAULT_FONT_SIZE;
        return fontSize * 2.32 + 1; // Ratio that seems to apply with large screen resolution
    }

    handleTopRowChange = (rowId: number | string) => {
        if (Utils.isExists(rowId)) {
            if (typeof rowId === 'number') {
                this.topRowIdx = rowId;
            } else {
                // TODO : we don't currently support row's being identified by a key.
            }
        } else {
            this.topRowIdx = 0;
        }
    };

    handleSelectionChange = (selectedRowKeys: Key[]) => {
        if (Utils.isNotEquals(selectedRowKeys, this.state.selectedRowKeys) && this.props.selection) {
            // Multiple selection
            if (this.props.selection.selectionMode === MULTIPLE_SELECTION) {
                this.setState({ selectedRowKeys });
                if (this.props.selection.onSelectionChange)
                    this.props.selection.onSelectionChange(
                        selectedRowKeys.map(rowKey => {
                            return this.props.data[rowKey];
                        }),
                    );
                // Single Selection (default)
            } else {
                const lastSelected = selectedRowKeys.find(
                    selected => this.state.selectedRowKeys.indexOf(selected) === -1,
                );
                if (lastSelected !== undefined) {
                    this.setState({ selectedRowKeys: [lastSelected] });
                    if (this.props.selection.onSelectionChange)
                        this.props.selection.onSelectionChange(this.props.data[lastSelected]);

                    // De-select by clicking on the already selected row
                } else {
                    this.setState({ selectedRowKeys: [] });
                    if (this.props.selection.onSelectionChange) this.props.selection.onSelectionChange(undefined);
                }
            }
        }
    };

    handleColumnWidthsChange = (nextColumnWidths: Array<TableColumnWidthInfo>) => {
        const columnWidth = new Map();
        nextColumnWidths.forEach(columnWidthInfo => {
            columnWidth.set(columnWidthInfo.columnName, columnWidthInfo.width);
        });
        this.setState({ columnWidth: columnWidth });
    };

    render() {
        const columns = this.props.columns.map(column => ({
            title: columnName(column), //setting the title with the unique columnName so it can be used in the title component to find its managed column object
            name: columnName(column),
            getCellValue: cv => column.getCellValue(cv, this),
        }));

        //here its creating a map with the different formatters and its associated columns
        //in order to then create only one DataTypeProvider of a kind
        const formattersMap = new Map<Function, string[]>();
        for (const column of this.props.columns) {
            if (column.extra?.formatter) {
                if (!formattersMap.get(column.extra.formatter)) {
                    formattersMap.set(column.extra.formatter, []);
                }
                formattersMap.get(column.extra.formatter).push(columnName(column));
            }
        }

        const dataTypeProviders = [];
        formattersMap.forEach((columnNames, formatterFunc) => {
            dataTypeProviders.push(
                <DataTypeProvider
                    key={columnNames.toString()}
                    for={columnNames}
                    formatterComponent={({ value }) => formatterFunc(value, this)}
                />,
            );
        });

        const columnExtensions = this.props.columns.map(
            column =>
                ({
                    columnName: columnName(column),
                    width: this.state.columnWidth.get(columnName(column)),
                    wordWrapEnabled: column.extra?.wordWrapEnabled,
                }) as VirtualTable.ColumnExtension,
        );

        const columnWidth = this.props.columns.map(
            column =>
                ({
                    columnName: columnName(column),
                    width: this.state.columnWidth.get(columnName(column)),
                }) as TableColumnWidthInfo,
        );

        // Sorting is enabled by default - only disable columns that explicitly disable sorting
        const sortingStateColumnExtensions = this.props.columns
            .filter(column => column.extra?.sortingEnabled === false)
            .map(
                column =>
                    ({
                        columnName: columnName(column),
                        sortingEnabled: false,
                    }) as SortingState.ColumnExtension,
            );

        const integratedSortingColumnExtensions = this.props.columns
            .filter(column => column.extra?.comparator)
            .map(
                column =>
                    ({
                        columnName: columnName(column),
                        compare: column.extra?.comparator,
                    }) as IntegratedSorting.ColumnExtension,
            );

        const columnBands = this.props.columns
            .map(column => column.extra?.columnBand)
            .filter((columnBand, index, self) => columnBand && self.indexOf(columnBand) === index)
            .map(columnBand => {
                const columns = this.props.columns
                    .filter(column => column.extra?.columnBand === columnBand)
                    .map(column => {
                        return { columnName: columnName(column) } as TableBandHeader.ColumnBands;
                    });
                return {
                    title: columnBand,
                    children: columns,
                } as TableBandHeader.ColumnBands;
            });

        const noDataCell = props => {
            return (
                <VirtualTable.NoDataCell
                    {...props}
                    getMessage={
                        this.props.noDataMessage !== undefined
                            ? messageKey => this.props.noDataMessage
                            : props.getMessage
                    }
                />
            );
        };

        const TableBandHeaderCell = props => <TableBandHeader.Cell {...props} style={{ textAlign: 'center' }} />;

        // Apply custom styles to cells, if part of the column definition
        const TableCell = props => {
            const column = this.props.columns.find(item => columnName(item) === props.column.name);
            const extraStyle = column.extra?.cellStyle?.(props.value, props.row);
            const className = column.extra?.className?.(props.value, props.row);
            const style = Object.assign({}, props.style, extraStyle);
            return <VirtualTable.Cell {...props} style={style} className={className} />;
        };

        // This plugin should only be used if column bands are being defined
        const tableBandHeaderPlugin =
            columnBands.length > 0 ? (
                <TableBandHeader
                    columnBands={columnBands}
                    cellComponent={TableBandHeaderCell}
                    rowComponent={TableBandHeader.Row}
                />
            ) : undefined;

        // Column resizing is not supported when using column bands
        // It also disabled when specified
        const tableColumnResizingPlugin =
            columnBands.length === 0 && !this.props.columnResizeDisabled ? (
                <TableColumnResizing columnWidths={columnWidth} onColumnWidthsChange={this.handleColumnWidthsChange} />
            ) : undefined;

        const sortMode = this.getSortMode();

        //the props.children here contains the columnName value so we could retrieve its definition from the props.columns
        //and show the "real" header text
        const TitleComponent = props => {
            const column: ManagedColumn<any> = this.props.columns.find(item => columnName(item) === props.children);
            return <TableColumnTooltipComponent column={column}>{column.header}</TableColumnTooltipComponent>;
        };

        const SortLabelComponent = ({ onSort, children, direction, disabled }) => {
            if (disabled) {
                // keeping the class sortable just because of the cursor (to look nicer)
                return <span className="sortable">{children}</span>;
            } else {
                return (
                    <span
                        role="button"
                        className="sortable"
                        onClick={event => {
                            SortHandler(onSort, direction, event, disabled);
                        }}>
                        {children}
                        {direction && (direction === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
                    </span>
                );
            }
        };

        //custom sort handler where it sets the keepOther to true if ctrl key is pressed -> multiple sorting
        const SortHandler = (onSort, direction, event, disabled) => {
            //only call onSort if the sort is enabled
            if (!disabled) onSort({ keepOther: event.ctrlKey });
        };

        return (
            // The dx-react-grid creates problem when the parent inner panel is resized, and width is below table width.
            // When this happens, part of the table is displayed outside of the inner panel.
            // The only way found is to wrap it around with a div which width is set to be the parent's width.
            <AutoSizer>
                {({ height, width }) => {
                    this.height = height;
                    return (
                        <div className="managed-table" style={{ height: height, width: width }}>
                            <Spin spinning={this.props.loading || false} tip="Loading data">
                                <Grid rows={this.props.data ? this.props.data : Array<T>()} columns={columns}>
                                    <SelectionState
                                        selection={this.state.selectedRowKeys}
                                        onSelectionChange={this.handleSelectionChange}
                                    />
                                    {sortMode !== SortMode.NONE && (
                                        <SortingState
                                            defaultSorting={this.props.defaultSorting}
                                            sorting={this.props.sorting}
                                            onSortingChange={this.props.onSortingChange}
                                            columnExtensions={sortingStateColumnExtensions}
                                        />
                                    )}
                                    {this.props.summaryRow && (
                                        <SummaryState totalItems={this.props.summaryRow.totalItems} />
                                    )}

                                    {sortMode === SortMode.INTERNAL && (
                                        <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
                                    )}
                                    {this.props.summaryRow && <IntegratedSummary />}

                                    <VirtualTable
                                        estimatedRowHeight={
                                            Utils.isExists(this.props.estimatedRowHeight)
                                                ? this.props.estimatedRowHeight
                                                : this.getEstimatedRowHeight()
                                        }
                                        height={height}
                                        noDataCellComponent={noDataCell}
                                        columnExtensions={columnExtensions}
                                        cellComponent={TableCell}
                                        ref={this.virtualTableRef}
                                        onTopRowChange={this.handleTopRowChange}
                                    />
                                    {tableColumnResizingPlugin}
                                    <TableHeaderRow
                                        sortLabelComponent={SortLabelComponent}
                                        showSortingControls={sortMode !== SortMode.NONE}
                                        titleComponent={TitleComponent}
                                    />
                                    {this.props.summaryRow && <TableSummaryRow />}
                                    <TableSelection selectByRowClick highlightRow showSelectionColumn={false} />
                                    <ConditionalRender condition={this.props.webBehaviour}>
                                        <WebBehaviour
                                            highlighter={this.props.highlighter}
                                            highlight={this.props.highlight}
                                        />
                                    </ConditionalRender>
                                    <ConditionalRender condition={!this.props.webBehaviour}>
                                        <DesktopBehaviour
                                            highlighter={this.props.highlighter}
                                            highlight={this.props.highlight}
                                        />
                                    </ConditionalRender>
                                    {/*<WebBehaviour*/}
                                    {/*	highlighter={this.props.highlighter}/>*/}

                                    {tableBandHeaderPlugin}

                                    {dataTypeProviders}

                                    {/* Required to retrieve rows in the displayed order*/}
                                    <Template name="root">
                                        <TemplateConnector>
                                            {({ rows: displayedRows }) => {
                                                this.displayedRows = displayedRows;
                                                return <TemplatePlaceholder />;
                                            }}
                                        </TemplateConnector>
                                    </Template>
                                </Grid>
                            </Spin>
                        </div>
                    );
                }}
            </AutoSizer>
        );
    }
}
